"use client";
import type { FC, RefObject } from "react";
import type { BoxProps } from "../../../layout/box/Box";
import { Box } from "../../../layout/box/Box";
import { ChevronLeftIcon, ChevronRightIcon, PauseIcon, PlayIcon } from "../Icons";
import { styles } from "../CarouselStyles";
import type { FlexProps } from "../../../layout/flex/Flex";
import { Flex } from "../../../layout/flex/Flex";
import type { SwiperClass } from "swiper/react";
import { IconButton } from "../../../forms/icon-button/IconButton";

export type SwipeCarouselVideoPlayback = {
    isEnabled: boolean;
    isPlaying: boolean;
    onPlayingChange: (isPlaying: boolean) => void;
};

type CarouselProductImageNavigationProps = FlexProps & {
    containerStyle?: object;
    previousSlideTitle?: string;
    nextSlideTitle?: string;
    playPauseTitle?: string;
    navsx?: {
        rightButton?: BoxProps["sx"];
        leftButton?: BoxProps["sx"];
    };
    swiperRef?: RefObject<SwiperClass | null>;
    videoPlayback?: SwipeCarouselVideoPlayback;
    previousButtonClassName?: string;
    nextButtonClassName?: string;
};

export const SwipeCarouselNavigation: FC<CarouselProductImageNavigationProps> = ({
    containerStyle,
    previousSlideTitle = "",
    nextSlideTitle = "",
    navsx = {
        rightButton: {},
        leftButton: {},
    },
    swiperRef,
    playPauseTitle = "",
    videoPlayback,
    previousButtonClassName,
    nextButtonClassName,
}) => {
    const { rightButton, leftButton } = navsx;
    const isVideoPlaying = videoPlayback?.isPlaying ?? false;

    const playPauseAriaLabel =
        playPauseTitle || (isVideoPlaying ? "Pause carousel" : "Play carousel");

    const handlePlayPauseClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();

        const nextIsPlaying = !isVideoPlaying;

        if (nextIsPlaying) {
            swiperRef?.current?.autoplay?.start();
        } else {
            swiperRef?.current?.autoplay?.stop();
        }

        videoPlayback?.onPlayingChange(nextIsPlaying);
    };

    return (
        <Box sx={styles.iconIndicatorContainer}>
            <Box sx={styles.iconIndicatorSubContainer}>
                <Flex data-testid="swiper-controls" sx={containerStyle}>
                    <IconButton
                        className={previousButtonClassName}
                        variant={"standard"}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={previousSlideTitle}
                        icon={<ChevronLeftIcon />}
                        sx={{ ...styles.iconButton, ...leftButton }}
                    />
                    <IconButton
                        className={nextButtonClassName}
                        variant={"standard"}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={nextSlideTitle}
                        icon={<ChevronRightIcon />}
                        sx={{ ...styles.iconButton, ...rightButton }}
                    />
                    {videoPlayback?.isEnabled && (
                        <IconButton
                            className="swiper-button-playpause"
                            variant={"standard"}
                            aria-label={playPauseAriaLabel}
                            icon={isVideoPlaying ? <PauseIcon /> : <PlayIcon />}
                            onClick={handlePlayPauseClick}
                            {...styles.iconButton}
                        />
                    )}
                </Flex>
            </Box>
        </Box>
    );
};

SwipeCarouselNavigation.displayName = "SwipeCarouselNavigation";
